import type { KnowledgeMemberRole } from '../../types/knowledge';

export type KdbPermission =
  | 'viewDocuments'
  | 'manageDocuments'
  | 'viewCrawls'
  | 'manageCrawls'
  | 'viewMembers'
  | 'manageMembers'
  | 'leaveKdb'
  | 'editDatabase'
  | 'deleteDatabase';

const KDB_ROLE_PERMISSIONS: Record<
  KnowledgeMemberRole,
  ReadonlySet<KdbPermission>
> = {
  ADMIN: new Set([
    'viewDocuments',
    'manageDocuments',
    'viewCrawls',
    'manageCrawls',
    'viewMembers',
    'manageMembers',
    'leaveKdb',
    'editDatabase',
    'deleteDatabase',
  ]),
  EDITOR: new Set([
    'viewDocuments',
    'manageDocuments',
    'viewCrawls',
    'manageCrawls',
    'viewMembers',
    'leaveKdb',
    'editDatabase',
  ]),
  VIEWER: new Set(['viewDocuments', 'viewCrawls', 'viewMembers', 'leaveKdb']),
};

function hasKdbPermission(
  role: KnowledgeMemberRole | null | undefined,
  permission: KdbPermission,
): boolean {
  if (!role) {
    return false;
  }
  return KDB_ROLE_PERMISSIONS[role].has(permission);
}

export function canEditKdb(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return hasKdbPermission(role, 'editDatabase');
}

export function canDeleteKdb(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return hasKdbPermission(role, 'deleteDatabase');
}

export function canManageKdbDocuments(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return hasKdbPermission(role, 'manageDocuments');
}

export function canManageKdbCrawls(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return hasKdbPermission(role, 'manageCrawls');
}

export function canManageKdbMembers(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return hasKdbPermission(role, 'manageMembers');
}

export function canLeaveKdb(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return hasKdbPermission(role, 'leaveKdb');
}

/** Admins may change role / remove any other member, including other admins. */
export function canManageKdbMemberTarget(
  actorRole: KnowledgeMemberRole | null | undefined,
  targetUserId: string | undefined,
  currentUserId: string | undefined,
): boolean {
  if (!canManageKdbMembers(actorRole) || !currentUserId || !targetUserId) {
    return false;
  }
  return targetUserId !== currentUserId;
}

/** Creating a chatbot requires EDITOR or ADMIN on the connected knowledge database. */
export function canCreateChatbotFromKnowledgeBase(
  role: KnowledgeMemberRole | null | undefined,
): boolean {
  return role === 'ADMIN' || role === 'EDITOR';
}
